<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';

import { useStore } from '@/store';
import { useI18n } from '@/locales';
import { useRouter, useToast } from '@base/hooks';
import BaseSelectColor from '@/components/base/BaseSelectColor.vue';
import { toBase64 } from '@base/utils/file';
import { required } from '@/modules/base/validators/composition';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const store = useStore();
const toast = useToast();
const { t } = useI18n();
const rules = {
  name: { required },
  color: { required },
  contentName: { required },
  contentUrl: { required },
};

const templateModel = reactive({ name: '', color: '#1474f5', contentName: '', contentUrl: '' });
const v$ = useVuelidate(rules, templateModel, { $autoDirty: true });
const loading = ref(false);
const templateError = ref(null);

const template = computed(() => store.getters['templateSetup/getTemplate']);
const acceptedMimeTypes = computed(() => {
  return ['.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].join(
    ', '
  );
});

const saveTemplate = async () => {
  try {
    loading.value = true;
    await store.dispatch('templateSetup/updateTemplate', { template: templateModel });
    toast.success(t('template.setup.edit.successMessage'));
    router.push({ name: 'templates' });
  } catch (err) {
    const errors = err.response.data.template.errors.content;
    if (errors.length) {
      const [firstError] = errors;
      templateError.value = firstError;
      toast.error(firstError);
    } else {
      toast.error(t('template.setup.edit.errorMessage'));
    }
  } finally {
    loading.value = false;
  }
};

const createTemplate = async () => {
  try {
    loading.value = true;
    await store.dispatch('templateSetup/createTemplate', templateModel);
    toast.success(t('template.setup.create.successMessage'));
    router.push({ name: 'templates' });
  } catch (err) {
    const errors = err.response.data.template.errors.content;
    if (errors.length) {
      const [firstError] = errors;
      templateError.value = firstError;
      toast.error(firstError);
    } else {
      toast.error(t('template.setup.create.errorMessage'));
    }
  } finally {
    loading.value = false;
  }
};

const handleTemplateInput = async (event) => {
  const docxMimetype = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const file = event.target.files[0];

  if (file.type !== docxMimetype) {
    this.$toast.error(this.$t('template.setup.uploadFormatError'));
    return;
  }

  const fileBase64 = await toBase64(file);
  templateModel.contentUrl = fileBase64;
  templateModel.contentName = file.name;
};

const handleFormInput = () => {
  if (props.isEditing) {
    saveTemplate();
  } else {
    createTemplate();
  }
};

onMounted(() => {
  if (props.isEditing) {
    templateModel.contentName = template.value.contentName;
    templateModel.contentUrl = template.value.contentUrl;
    templateModel.name = template.value.name;
    templateModel.color = template.value.color;
  }
});
</script>

<template>
  <div>
    <section :class="$style.formWrap">
      <XTextInput
        v-model="templateModel.name"
        :label="$t('template.setup.nameLabel')"
        autofocus
        required
        full-width
        data-testid="templateNameField"
      />
      <XFormField
        :label="$t('template.setup.uploadLabel')"
        :message="templateError"
        full-width
      >
        <XTextInput
          :value="templateModel.contentName"
          full-width
          disabled
          data-testid="documentFileUploadText"
        />
        <XFileInput
          :accept="acceptedMimeTypes"
          data-testid="documentFileUploadField"
          @input="handleTemplateInput"
        >
          <template #default="{ handleFileUpload }">
            <XButton
              size="sm"
              design="outlined"
              @click="handleFileUpload"
            >
              <XIcon
                icon="file-upload"
                left
              />
              {{ $t('template.setup.uploadPlaceholder') }}
            </XButton>
          </template>
        </XFileInput>
      </XFormField>
      <div>
        <XLink
          data-ga-event="link-download-template-example-click"
          no-padding
          to="/docs/Contrato de Prestação de Serviço - Exemplo com imagem.docx"
        >
          {{ $t('template.setup.documentExample') }}
          <XIcon icon="arrow-down-to-line" />
        </XLink>
      </div>
      <div :class="$style.selectColor">
        <label :class="$style.colorLabel">
          {{ $t('template.setup.colorLabel') }}
        </label>

        <BaseSelectColor v-model="templateModel.color" />

        <div :class="$style.iconColorSelected">
          <XIcon
            icon="file-invoice"
            size="lg"
            :style="{ color: templateModel.color }"
          />
        </div>
      </div>
    </section>

    <div :class="$style.containerButton">
      <XButton
        size="sm"
        :class="$style.button"
        :loading="loading"
        :disabled="v$.$invalid"
        data-testid="saveTemplateConfirmButton"
        @click="handleFormInput"
      >
        {{ isEditing ? $t('template.setup.save') : $t('template.setup.createTemplate') }}
      </XButton>
    </div>
  </div>
</template>

<style lang="scss" module>
.formWrap {
  display: flex;
  flex-direction: column;
  max-width: 472px;
  gap: var(--space-medium);
}

.selectColor {
  .colorLabel {
    margin-bottom: var(--space-small-xx);
    font-weight: var(--font-weight-regular);
  }

  .iconColorSelected {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-top: var(--space-regular);
    border: var(--border-width-small) solid var(--color-feedback-info-50);
    border-radius: var(--border-radius-small);
  }
}

.containerButton {
  display: flex;
  justify-content: flex-start;

  .button {
    margin-top: var(--space-regular);
  }
}
</style>
